import { useCallback, useEffect, useState } from 'react';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/Button';
import { TablePagination } from '@/components/ui/TablePagination';
import { financeApi } from '@/api/finance.api';
import { usePagination } from '@/hooks/usePagination';
import { showApiError } from '@/utils/apiError';
import type { FinExpense } from '@/types/finance';

export function FinanceExpensesPage() {
  const pagination = usePagination();
  const [expenses, setExpenses] = useState<FinExpense[]>([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await financeApi.listExpenses({ page: pagination.page, size: pagination.size });
      setExpenses(r.content);
      pagination.applyPageResponse(r);
    } catch (e) {
      showApiError(e, 'Failed to load expenses');
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.size, pagination]);

  useEffect(() => {
    void load();
  }, [load]);

  async function createExpense() {
    const serial = Date.now().toString().slice(-6);
    try {
      await financeApi.createExpense({
        expenseNumber: `EXP-${serial}`,
        expenseDate: new Date().toISOString().slice(0, 10),
        currencyCode: 'USD',
        categoryCode: 'GENERAL',
        items: [{ description: 'Office supplies', amount: 50 }],
      });
      void load();
    } catch (e) {
      showApiError(e, 'Failed to create expense');
    }
  }

  async function approve(id: number) {
    try {
      await financeApi.approveExpense(id);
      void load();
    } catch (e) {
      showApiError(e, 'Failed to approve expense');
    }
  }

  return (
    <div>
      <PageHeader
        title="Expenses"
        description="Expense claims and approvals — approved expenses post to the general ledger"
        breadcrumbs={[{ label: 'Org', to: '/org' }, { label: 'Finance', to: '/finance' }, { label: 'Expenses' }]}
      />
      <div className="card p-4">
        <Button type="button" className="mb-4" onClick={createExpense}>
          New expense
        </Button>
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b">
              <th className="p-2 text-left">Number</th>
              <th className="p-2 text-left">Date</th>
              <th className="p-2 text-right">Amount</th>
              <th className="p-2 text-left">Status</th>
              <th className="p-2" />
            </tr>
          </thead>
          <tbody>
            {expenses.map((e) => (
              <tr key={e.id} className="border-b">
                <td className="p-2">{e.expenseNumber}</td>
                <td className="p-2">{e.expenseDate}</td>
                <td className="p-2 text-right font-mono">{e.totalAmount}</td>
                <td className="p-2">{e.statusCode}</td>
                <td className="p-2 text-right">
                  {e.statusCode === 'DRAFT' && (
                    <button type="button" className="text-primary-600 text-xs" onClick={() => approve(e.id)}>
                      Approve
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <TablePagination
          page={pagination.page}
          totalPages={pagination.totalPages}
          totalElements={pagination.totalElements}
          onPageChange={pagination.setPage}
          loading={loading}
          hasRows={expenses.length > 0}
        />
      </div>
    </div>
  );
}
